import React, { useEffect } from 'react'
import { createPortal } from 'react-dom'
import logoInduma from '../assets/logo-induma.png'

// ─── Tamaños disponibles ──────────────────────────────────────────────────────
const SIZES = {
  small:  { spinner: 40, logo: 24, font: '14px', gap: '16px' },
  medium: { spinner: 56, logo: 36, font: '18px', gap: '24px' },
  large:  { spinner: 72, logo: 48, font: '20px', gap: '32px' },
}

// ─── Delegación al overlay global del index.html ─────────────────────────────
function LoadingOverlayGlobal ({ message }) {
  useEffect(() => {
    window.__showLoadingOverlay(message || 'Cargando...')
    return () => { window.__hideLoadingOverlay && window.__hideLoadingOverlay() }
  }, [message])
  return null
}

// ─── Contenido visual del spinner ────────────────────────────────────────────
function SpinnerContent ({ message, size = 'medium', showLogo = true }) {
  const s = SIZES[size] || SIZES.medium

  return (
    <>
      <style>{`
        @keyframes __loading-spin { to { transform: rotate(360deg); } }
      `}</style>
      <div style={{
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', gap: s.gap,
      }}>
        {/* Spinner + logo superpuesto */}
        <div style={{ position: 'relative', width: s.spinner, height: s.spinner }}>
          {/* Anillo giratorio */}
          <div style={{
            width: s.spinner, height: s.spinner,
            border: '3px solid rgba(0,0,0,0.08)',
            borderTopColor: '#2563eb',
            borderRadius: '50%',
            animation: '__loading-spin 0.9s linear infinite',
            boxSizing: 'border-box',
          }} aria-hidden />
          {/* Logo centrado dentro del anillo */}
          {showLogo && (
            <div style={{
              position: 'absolute', inset: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <img
                src={logoInduma}
                alt="INDUMA"
                width={s.logo}
                height={s.logo}
                style={{ width: s.logo, height: s.logo, objectFit: 'contain', background: 'transparent' }}
                onError={(e) => {
                  if (!e.target.getAttribute('data-fallback')) {
                    e.target.setAttribute('data-fallback', '1')
                    e.target.src = '/logo-induma.png'
                  } else {
                    e.target.style.display = 'none'
                  }
                }}
              />
            </div>
          )}
        </div>
        {/* Mensaje */}
        {message && (
          <p style={{
            margin: 0, fontSize: s.font, fontWeight: 700,
            color: '#111827', letterSpacing: '-0.01em',
          }}>
            {message}
          </p>
        )}
      </div>
    </>
  )
}

/**
 * Overlay de carga global y reutilizable.
 *
 * Props:
 *   message  {string}  — Texto debajo del spinner. Default: 'Cargando...'
 *   size     {string}  — 'small' | 'medium' | 'large'. Default: 'medium'
 *   showLogo {boolean} — Mostrar logo INDUMA dentro del spinner. Default: true
 *   inline   {boolean} — Si true, renderiza en línea (no como overlay full-screen). Default: false
 */
const LoadingOverlay = ({ message = 'Cargando...', size = 'medium', showLogo = true, inline = false }) => {

  // Modo inline: spinner centrado en el espacio disponible, sin overlay full-screen
  if (inline) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '48px 0', width: '100%',
      }} role="status" aria-live="polite" aria-busy="true">
        <SpinnerContent message={message} size={size} showLogo={showLogo} />
      </div>
    )
  }

  // Modo overlay: delegar al overlay global de index.html si existe
  if (typeof window !== 'undefined' && window.__showLoadingOverlay) {
    return <LoadingOverlayGlobal message={message} />
  }

  // Fallback SSR
  if (typeof document === 'undefined' || !document.body) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f9fafb' }}>
        <p style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#374151' }}>{message}</p>
      </div>
    )
  }

  // Overlay full-screen como portal
  const content = (
    <div
      data-loading-overlay
      style={{
        position: 'fixed', inset: 0, width: '100vw', height: '100vh',
        zIndex: 2147483647, background: '#f9fafb',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        pointerEvents: 'auto',
      }}
      role="alert"
      aria-live="polite"
      aria-busy="true"
    >
      <SpinnerContent message={message} size={size} showLogo={showLogo} />
    </div>
  )

  return createPortal(content, document.body)
}

export default LoadingOverlay
